// Professional Mode Toggle - Healthcare Provider Experience
import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Stethoscope } from 'lucide-react';
import { cn } from '@/lib/utils';

const PROFESSIONAL_MODE_KEY = 'nathof_professional_mode';

interface ProfessionalModeContextValue {
  isProfessionalMode: boolean;
  toggleProfessionalMode: () => void;
  enableProfessionalMode: () => void;
  disableProfessionalMode: () => void;
}

const ProfessionalModeContext = createContext<ProfessionalModeContextValue | undefined>(undefined);

export const ProfessionalModeProvider = ({ children }: { children: ReactNode }) => {
  const [isProfessionalMode, setIsProfessionalMode] = useState(false);

  useEffect(() => {
    // Check URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('profissional') === '1') {
      setIsProfessionalMode(true);
      localStorage.setItem(PROFESSIONAL_MODE_KEY, 'true');
      return;
    }

    // Check localStorage
    const stored = localStorage.getItem(PROFESSIONAL_MODE_KEY);
    if (stored === 'true') {
      setIsProfessionalMode(true);
    }
  }, []);

  const toggleProfessionalMode = () => {
    const newValue = !isProfessionalMode;
    setIsProfessionalMode(newValue);
    localStorage.setItem(PROFESSIONAL_MODE_KEY, String(newValue));
  };

  const enableProfessionalMode = () => {
    setIsProfessionalMode(true);
    localStorage.setItem(PROFESSIONAL_MODE_KEY, 'true');
  };

  const disableProfessionalMode = () => {
    setIsProfessionalMode(false);
    localStorage.removeItem(PROFESSIONAL_MODE_KEY);
  };

  return (
    <ProfessionalModeContext.Provider value={{
      isProfessionalMode,
      toggleProfessionalMode,
      enableProfessionalMode,
      disableProfessionalMode
    }}>
      {children}
    </ProfessionalModeContext.Provider>
  );
};

export const useProfessionalMode = () => {
  const context = useContext(ProfessionalModeContext);
  if (context === undefined) {
    throw new Error('useProfessionalMode must be used within a ProfessionalModeProvider');
  }
  return context;
};

// Discrete toggle link for footer or hidden areas
interface ProfessionalModeLinkProps {
  className?: string;
}

export const ProfessionalModeLink = ({ className }: ProfessionalModeLinkProps) => {
  return (
    <Link
      to="/b2b"
      className={cn(
        "text-xs text-secondary-foreground/80 hover:text-secondary-foreground transition-colors flex items-center gap-1",
        className
      )}
    >
      <Stethoscope className="w-3 h-3" />
      <span>Sou profissional de saúde</span>
    </Link>
  );
};

// Wrapper component to hide content in professional mode
interface HideInProfessionalModeProps {
  children: ReactNode;
  showInstead?: ReactNode;
}

export const HideInProfessionalMode = ({ children, showInstead }: HideInProfessionalModeProps) => {
  const { isProfessionalMode } = useProfessionalMode();

  if (isProfessionalMode) {
    return showInstead ? <>{showInstead}</> : null;
  }

  return <>{children}</>;
};

// Wrapper component to show content only in professional mode
interface ShowInProfessionalModeProps {
  children: ReactNode;
}

export const ShowInProfessionalMode = ({ children }: ShowInProfessionalModeProps) => {
  const { isProfessionalMode } = useProfessionalMode();

  if (!isProfessionalMode) {
    return null;
  }

  return <>{children}</>;
};
